/**
 * TR-2 — the single source of truth for the Training section's navigation:
 * the seven screens, their labels/icons/routes, the sidebar grouping, and the
 * sidebar badges. The grouping is DERIVED from the content module's
 * `navSections` (Overview / Study / Test) so the shell can never drift from the
 * curriculum's own section map.
 *
 * Badges mirror the design's `navBadge` exactly: review shows the missed count
 * (a red "alert" pill, hidden at zero), learn and cards show `done/total`. The
 * totals come from the content module; the `done`/`missed` numbers arrive with
 * the progress store in TR-5 — until then they are honestly zero.
 */
import type { ComponentType } from 'react';
import {
  LayoutDashboard,
  MapPin,
  BookOpen,
  Layers,
  CheckCircle2,
  ClipboardList,
  RotateCcw,
} from 'lucide-react';
import { navSections, lessons, flashcards } from '@/content/training';

export const TRAINING_BASE = '/training';

export type TrainingKey =
  | 'home'
  | 'roadmap'
  | 'learn'
  | 'cards'
  | 'practice'
  | 'exam'
  | 'review';

export interface TrainingNavItem {
  key: TrainingKey;
  /** Nav label — verbatim from the design's NAV array. */
  name: string;
  /** Absolute route path. `home` is the index route (== TRAINING_BASE). */
  path: string;
  /** Exact-match active check (only `home`, which would otherwise match every child). */
  end: boolean;
  icon: ComponentType<{ className?: string }>;
}

type Icon = ComponentType<{ className?: string }>;

// label + icon per screen, verbatim from the design's NAV array (lucide icons
// standing in for the design's inline SVGs). One place to add the eventual
// "bites" study mode, which lives inside Learn and has no sidebar row of its own.
const META: Record<TrainingKey, { name: string; icon: Icon }> = {
  home: { name: 'Dashboard', icon: LayoutDashboard },
  roadmap: { name: 'CDL Roadmap', icon: MapPin },
  learn: { name: 'Learn', icon: BookOpen },
  cards: { name: 'Flashcards', icon: Layers },
  practice: { name: 'Practice', icon: CheckCircle2 },
  exam: { name: 'Exam Simulator', icon: ClipboardList },
  review: { name: 'Missed Questions', icon: RotateCcw },
};

export function trainingPath(key: TrainingKey): string {
  return key === 'home' ? TRAINING_BASE : `${TRAINING_BASE}/${key}`;
}

export const TRAINING_NAV_ITEMS: TrainingNavItem[] = (
  Object.keys(META) as TrainingKey[]
).map((key) => ({
  key,
  name: META[key].name,
  path: trainingPath(key),
  end: key === 'home',
  icon: META[key].icon,
}));

const BY_KEY: Record<string, TrainingNavItem> = Object.fromEntries(
  TRAINING_NAV_ITEMS.map((i) => [i.key, i]),
);

export interface TrainingNavSection {
  title: string;
  items: TrainingNavItem[];
}

/** Sidebar groups, derived from the content module's navSections. */
export const TRAINING_NAV_SECTIONS: TrainingNavSection[] = navSections.map(
  ([title, keys]) => ({
    title,
    items: keys.map((k) => BY_KEY[k]).filter(Boolean),
  }),
);

export interface TrainingProgressCounts {
  lessonsDone: number;
  cardsKnown: number;
  missed: number;
}

/** No progress yet — the state until the TR-5 store lands. */
export const ZERO_PROGRESS: TrainingProgressCounts = {
  lessonsDone: 0,
  cardsKnown: 0,
  missed: 0,
};

export interface TrainingNavBadge {
  text: string;
  /** review renders red; learn/cards render as a neutral count pill. */
  alert?: boolean;
}

/**
 * The sidebar badge for a nav key, or null for none — mirrors the design's
 * `navBadge`: review = missed count (hidden at 0), learn/cards = done/total.
 */
export function navBadge(
  key: TrainingKey,
  progress: TrainingProgressCounts = ZERO_PROGRESS,
): TrainingNavBadge | null {
  if (key === 'review') {
    return progress.missed > 0 ? { text: String(progress.missed), alert: true } : null;
  }
  if (key === 'learn') return { text: `${progress.lessonsDone}/${lessons.length}` };
  if (key === 'cards') return { text: `${progress.cardsKnown}/${flashcards.length}` };
  return null;
}

/** True when the given location is anywhere inside the Training section. */
export function isTrainingPath(pathname: string): boolean {
  return pathname === TRAINING_BASE || pathname.startsWith(`${TRAINING_BASE}/`);
}
